import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../config/react-query.config';
import { organizationService, userService } from '../../../services';
import { organizationApi } from '../../services/api/organization.api';
import { Organization } from '../../types';

/**
 * Fetch all organizations for the current user
 *
 * Features:
 * - Automatic deduplication (multiple components = 1 API call)
 * - 30 second cache (configurable in react-query.config.ts)
 * - Automatic refetch on window focus
 * - Only runs when user is authenticated (prevents 401 errors on login)
 */
export function useUserOrganizations() {
  // Check if user has a valid token before enabling the query
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');

  return useQuery({
    queryKey: queryKeys.users.organizations(),
    queryFn: async () => {
      const response = await userService.getUserOrganizationsWithMetadata();
      console.log('🔍 useUserOrganizations: Raw API response:', response);
      const organizations = response.data || [];
      console.log('🔍 useUserOrganizations: Extracted organizations:', organizations);
      return organizations;
    },
    enabled: hasToken, // Only fetch when token is available
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Fetch a single organization by ID
 *
 * @param organizationId - The organization ID to fetch
 * @param options - Query options
 */
export function useOrganization(organizationId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.organizations.detail(organizationId!),
    queryFn: () => organizationService.getOrganizationById(organizationId!),
    enabled: !!organizationId, // Only fetch if organizationId exists
    staleTime: 30000,
  });
}

/**
 * Fetch members for a specific organization
 *
 * @param organizationId - The organization ID
 */
export function useOrganizationMembers(organizationId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.organizations.members(organizationId!),
    queryFn: () => organizationService.listOrganizationMembers(organizationId!),
    enabled: !!organizationId,
    staleTime: 30000,
  });
}

/**
 * Fetch invitations for a specific organization
 *
 * @param organizationId - The organization ID
 */
export function useOrganizationInvitations(organizationId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.organizations.invitations(organizationId!),
    queryFn: async () => {
      const response = await organizationApi.listInvitations(organizationId!);
      return response.data || [];
    },
    enabled: !!organizationId,
    staleTime: 30000,
  });
}

/**
 * Fetch subscriptions for a specific organization
 *
 * @param organizationId - The organization ID
 */
export function useOrganizationSubscriptions(organizationId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.organizations.subscriptions(organizationId!),
    queryFn: async () => {
      // organizationService.getOrganizationSubscriptions now returns array directly
      const response = await organizationService.getOrganizationSubscriptions(organizationId!);
      return response || [];
    },
    enabled: !!organizationId,
    staleTime: 30000,
  });
}

/**
 * Create a new organization
 *
 * Automatically invalidates the user organizations cache on success
 */
export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      organizationService.createOrganization(data),
    onSuccess: () => {
      // Invalidate and refetch user organizations
      queryClient.invalidateQueries({ queryKey: queryKeys.users.organizations() });
    },
  });
}

/**
 * Update an existing organization
 *
 * Automatically invalidates the organization cache on success
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Organization> }) =>
      organizationService.updateOrganization(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific organization and user organizations list
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.organizations() });
    },
  });
}

/**
 * Delete an organization
 *
 * Automatically invalidates the user organizations cache on success
 */
export function useDeleteOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => organizationService.deleteOrganization(id),
    onSuccess: () => {
      // Invalidate user organizations list
      queryClient.invalidateQueries({ queryKey: queryKeys.users.organizations() });
    },
  });
}
